/**
 * Canonical capture-setup standard for posture photography.
 *
 * Single source of truth for how a subject should be photographed. The funnel staging guide,
 * the guided-capture wizard and the practitioner help system all render from here.
 *
 * Why this module exists: before 2026-07-29 the app carried three different camera heights
 * ("chest level" in the practitioner help, "lower thoracic" in RuleDoc-2.1-Analysis-Postural,
 * and nothing at all in the self-assessment wizard). Prospects shot from eye level with the
 * phone tilted down, which biases the sagittal geometry the APT/swayback distinction rests on.
 * Keeping the values in one place is what stops that drift recurring.
 *
 * Navel height is deliberate: it sits near the vertical midpoint of a standing subject, so
 * perspective error above and below the lens is symmetric rather than one-directional, and it
 * places the optical axis on the pelvis — the region that decides the pattern. It is consistent
 * with published photogrammetry protocols, which put the lens at 80–115 cm for adult subjects.
 *
 * Presentation only. Nothing here feeds analysis, and nothing here may import from
 * `src/lib/posture-analysis/**` (see PLAN.md §0 — the scoring freeze).
 */

/** Minimum subject-to-camera distance, in metres. */
export const CAPTURE_MIN_DISTANCE_M = 2;

/** Minimum subject-to-camera distance, in feet — the imperial rendering of the same rule. */
export const CAPTURE_MIN_DISTANCE_FT = 6;

/** Anatomical reference for lens height. */
export const CAPTURE_CAMERA_HEIGHT_LANDMARK = 'navel';

/** Human-readable distance, e.g. "at least 2 m (6 ft)". */
export const CAPTURE_DISTANCE_TEXT = `at least ${CAPTURE_MIN_DISTANCE_M} m (${CAPTURE_MIN_DISTANCE_FT} ft)`;

export interface CaptureSetupRule {
  /** Stable key for tests and list rendering. */
  id: 'height' | 'distance' | 'framing' | 'obstructions';
  /** Short label — safe to show on a 360 px viewport without wrapping past two lines. */
  label: string;
  /** One sentence of detail, including the *why* where it changes behaviour. */
  detail: string;
}

/**
 * The four rules, in the order a user physically performs them: set the phone height, step
 * back, check the frame, clear the shot.
 */
export const CAPTURE_SETUP_RULES: readonly CaptureSetupRule[] = [
  {
    id: 'height',
    label: 'Camera at navel height',
    detail:
      'Prop the phone so the lens is level with your belly button — not at eye or chest height. Keep it upright rather than tilted down to fit you in.',
  },
  {
    id: 'distance',
    label: `Stand ${CAPTURE_DISTANCE_TEXT} back`,
    detail: 'Distance keeps the lens from distorting your proportions. Further is better than closer.',
  },
  {
    id: 'framing',
    label: 'Whole body in frame',
    detail: 'Head to feet visible, with a little space above and below.',
  },
  {
    id: 'obstructions',
    label: 'Clear the shot',
    detail:
      'Nothing between you and the camera, and nothing covering your hips — they anchor the analysis.',
  },
] as const;

/**
 * Compact reminder for the moment before the first shot (guided-capture wizard, step 1).
 * Deliberately one line: the wizard card is an overlay on a live camera feed.
 */
export const CAPTURE_SETUP_WIZARD_REMINDER = `Phone at navel height, ${CAPTURE_DISTANCE_TEXT} away, whole body in frame.`;

/** Heading for the always-visible pre-capture summary. */
export const CAPTURE_SETUP_SUMMARY_TITLE = 'Before you start';

/**
 * Practitioner-help rendering of the height rule. Kept here so the help system and the funnel
 * can never disagree again.
 */
export const CAPTURE_SETUP_HELP_HEIGHT_TEXT =
  'Position the camera at navel height and keep it level — do not tilt it down to fit the client in frame. Navel height keeps perspective distortion symmetric and puts the lens on the pelvis, which drives the sagittal classification.';

/** Practitioner-help rendering of the distance rule. */
export const CAPTURE_SETUP_HELP_DISTANCE_TEXT = `Position the camera ${CAPTURE_DISTANCE_TEXT} away — 8–10 ft is ideal. The full body should be visible with some space above the head and below the feet.`;
